import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/core/components/ui/button';
import { ChevronDown, Filter, Eye, EyeOff } from 'lucide-react';

export const CHARGING_STATUSES = [
  'Available',
  'Charging',
  'Faulted',
  'Finishing',
  'Preparing',
  'Reserved',
  'SuspendedEv',
  'SuspendedEvse',
  'Unavailable',
] as const;

export type ChargingStatus = typeof CHARGING_STATUSES[number];

interface ChargingStatusFilterProps {
  selectedStatuses: string[];
  onSelectedStatusesChange: (statuses: string[]) => void;
}

export const ChargingStatusFilter = ({
  selectedStatuses,
  onSelectedStatusesChange
}: ChargingStatusFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  // Local pending state — only committed on Apply or close
  const [pendingStatuses, setPendingStatuses] = useState<string[]>(selectedStatuses);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Sync pending state when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPendingStatuses(selectedStatuses);
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.right - 256,
          width: rect.width,
        });
      }
    }
  }, [isOpen, selectedStatuses]);

  const selectedCount = selectedStatuses.length;
  const hasActiveFilter = selectedCount > 0 && selectedCount < CHARGING_STATUSES.length;

  const applyAndClose = (statuses: string[]) => {
    onSelectedStatusesChange(statuses);
    setIsOpen(false);
  };

  const handleApply = () => applyAndClose(pendingStatuses);

  const handleBackdropClose = () => applyAndClose(pendingStatuses);

  const toggleStatus = (status: string) => {
    setPendingStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const showAll = () => setPendingStatuses([...CHARGING_STATUSES]);
  const hideAll = () => setPendingStatuses([]);

  const pendingCount = pendingStatuses.length;

  return (
    <div ref={buttonRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 text-sm ${
          hasActiveFilter ? 'bg-yellow-100 border-yellow-400 hover:bg-yellow-200' : ''
        }`}
      >
        <Filter className={`h-4 w-4 ${hasActiveFilter ? 'text-yellow-600' : ''}`} />
        <span>Status ({selectedCount}/{CHARGING_STATUSES.length})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && createPortal(
        <>
          {/* Backdrop — apply on close */}
          <div
            className="fixed inset-0 z-40"
            onClick={handleBackdropClose}
          />

          {/* Dropdown */}
          <div
            className="fixed w-64 bg-white border rounded-lg shadow-lg z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">Charging Status</span>
                <span className="text-xs text-gray-500">
                  {pendingCount} of {CHARGING_STATUSES.length} selected
                </span>
              </div>

              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={showAll} className="text-xs flex-1">
                  Show All
                </Button>
                <Button size="sm" variant="outline" onClick={hideAll} className="text-xs flex-1">
                  Hide All
                </Button>
              </div>
            </div>

            {/* Apply button — above status list */}
            <div className="px-3 py-2 border-b">
              <Button size="sm" className="w-full text-xs" onClick={handleApply}>
                Apply Filter
              </Button>
            </div>

            {/* All statuses visible — no scroll */}
            <div>
              {CHARGING_STATUSES.map((status) => {
                const isSelected = pendingStatuses.includes(status);
                return (
                  <div
                    key={status}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleStatus(status)}
                  >
                    <div className="flex items-center space-x-2">
                      {isSelected ? (
                        <Eye className="h-4 w-4 text-blue-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-sm ${isSelected ? 'text-gray-900' : 'text-gray-500'}`}>
                        {status}
                      </span>
                    </div>
                    <div className={`w-4 h-4 rounded border-2 ${
                      isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {isSelected && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                          <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </>,
        document.body
      )}
    </div>
  );
};
